import React, { useState } from 'react';
import SocialSignInButtons from 'components/Auth/SocialSignInButtons';
import { useNavigate } from 'react-router-dom';
import {
  Phone, AlertCircle, Send, CheckCircle, ArrowLeft, UserPlus, RefreshCw, MessageSquare,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { BRAND_CONFIG } from 'constants/branding';
import axiosInstance from 'api/axios.config';

/**
 * The sign-in screen, for every brand this portal wears.
 *
 * <h3>What the redesign removed, and why</h3>
 * The previous version scattered six translucent icon bubbles, three pulsing
 * SVG lines and two blurred orbs across the background. Several were pinned far
 * enough out that the viewport clipped them, so the first impression of the
 * product was half a circle hanging off the edge of the screen. None of it said
 * anything about the product, and motion on a login screen competes with the
 * only thing somebody came here to do.
 *
 * <h3>The capability list earns its place now</h3>
 * It used to be four bare labels — "Voice Calls", "Email Communication" — which
 * is a list of nouns anybody could guess from the name. Each line now says what
 * the platform actually does with that channel, because the panel is the only
 * part of this page doing any selling, and the audience includes people being
 * onboarded by a reseller who have never seen the product.
 *
 * <h3>The consent checkbox is gone, deliberately</h3>
 * It was rendered pre-ticked, from {@code terms.autoAccept}. A pre-ticked
 * consent box is worth nothing as consent — nobody chose it — and agreeing to
 * terms belongs at the moment an account is created, not on every sign-in
 * afterwards by someone who agreed months ago. The links stay, as the passive
 * line of small print that every other product uses, so the obligation is still
 * disclosed and the guard that could block a legitimate sign-in is gone.
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        // Back where they were headed, when something sent them here.
        //
        // The OAuth consent screen is the reason this exists: an assistant opens
        // /authorize, the person is not signed in, and dropping them on the
        // dashboard abandons the authorisation half-done with no way back to it.
        // Only a PATH is honoured — an absolute URL here would make the login
        // page an open redirect, which is worth more to an attacker than it is
        // to us.
        const next = new URLSearchParams(window.location.search).get('next');
        const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
        navigate(safeNext);
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setForgotError('');
    setForgotLoading(true);
    try {
      const response = await axiosInstance.post('/auth/forgot-password', { email: forgotEmail });
      if (response.data.success) {
        setForgotSuccess(true);
      } else {
        setForgotError(response.data.message || 'An error occurred');
      }
    } catch {
      // Still show success — a different answer for a known address would turn
      // this form into a way to discover who has an account.
      setForgotSuccess(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSuccess(false);
    setForgotError('');
  };

  /**
   * The brand mark.
   *
   * Honours {@code logo.iconName}, which the previous version declared in the
   * config and then ignored — every brand got a telephone, including the ones
   * whose product is not a phone system.
   */
  const BrandIcon = BRAND_CONFIG.logo.iconName === 'MessageSquare' ? MessageSquare : Phone;

  /**
   * What the platform does, in the panel's own words.
   *
   * Named integrations rather than "CRM sync" alone: the audience includes
   * people evaluating whether their existing stack is supported, and a vendor
   * they recognise answers that question where a category name does not. The
   * list matches OrganizationCrmProvider's own enum — if a provider is dropped
   * there, it must be dropped here too rather than left advertising itself.
   */
  const CAPABILITIES = [
    {
      icon: Phone,
      title: 'Voice, with agents that answer',
      body: 'Inbound and outbound calling, IVR and AI agents that hold a conversation and hand over cleanly.',
    },
    {
      icon: Send,
      title: 'WhatsApp, SMS, RCS and email',
      body: 'Template campaigns and two-way conversations, with delivery you can see per message.',
    },
    {
      icon: UserPlus,
      title: 'Leads, captured and owned',
      body: 'Every call, chat and form becomes a lead against one contact, with an owner and a status attached.',
    },
    {
      icon: RefreshCw,
      title: 'Synced to the CRM you already use',
      body: 'Zoho, LeadSquared, Zenoti, Shopify and n8n — with a retry queue, so an outage makes a lead late, never lost.',
    },
  ];

  const field =
    'w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-900 text-[15px] ' +
    'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 ' +
    'focus:border-transparent disabled:opacity-60 dark:bg-gray-800 dark:text-gray-100';
  const labelCls = 'block text-[13px] font-medium text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50">

      {/* ── Brand panel ──────────────────────────────────────────────────────
          A solid deep ground rather than a gradient wash: at this size a
          gradient only reads as a colour that could not decide. The rings are
          the one piece of decoration, anchored to a corner so nothing is ever
          clipped mid-shape, and drawn at an opacity that survives being ignored. */}
      <div className="relative lg:w-[46%] xl:w-[44%] bg-blue-950 text-white overflow-hidden
                      px-7 py-10 sm:px-12 lg:px-14 lg:py-14 flex flex-col">
        <svg aria-hidden="true" viewBox="0 0 400 400"
             className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] opacity-[0.13]">
          <g fill="none" stroke="currentColor" strokeWidth="1">
            <circle cx="200" cy="200" r="60" />
            <circle cx="200" cy="200" r="110" />
            <circle cx="200" cy="200" r="160" />
            <circle cx="200" cy="200" r="198" />
          </g>
        </svg>

        {/* Lockup. Always visible, at every width — below lg this panel collapses
            to a slim branded bar and becomes the ONLY place the brand appears,
            which is why the form does not repeat it. */}
        <div className="relative flex items-center gap-3">
          {BRAND_CONFIG.logo.imageUrl ? (
            /* On a white tile, in the same 40px rounded square the fallback uses.
               A brand mark with a transparent interior — CatchMyCall's is a head
               outline with sound-wave arcs inside it — loses those arcs against
               this dark panel: they are light-on-light by design. Sitting it on
               white shows the mark as drawn, and keeps the lockup the same shape
               whichever branch renders. */
            <span className="inline-flex items-center justify-center w-10 h-10 shrink-0 overflow-hidden rounded-xl bg-white ring-1 ring-white/25">
              <img
                src={BRAND_CONFIG.logo.imageUrl}
                alt={BRAND_CONFIG.brandName}
                className="h-8 w-8 object-contain"
              />
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.14] ring-1 ring-white/25">
              <BrandIcon className="w-[18px] h-[18px] text-white" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[17px] font-semibold leading-tight truncate">{BRAND_CONFIG.brandName}</p>
            {BRAND_CONFIG.brandOwner && (
              <p className="text-[12px] text-white/60 leading-tight truncate">{BRAND_CONFIG.brandOwner}</p>
            )}
          </div>
        </div>

        {/* The proposition. Hidden below lg, where the form is the whole point
            of the screen and a scroll to reach it is a worse first impression
            than no sales copy at all. */}
        <div className="relative hidden lg:flex flex-col justify-center flex-1 max-w-xl">
          <h1 className="text-[34px] xl:text-[40px] font-semibold leading-[1.1] tracking-[-0.02em] text-balance">
            {BRAND_CONFIG.brandTagline}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-white/70 max-w-md">
            One platform for every channel a customer reaches you on — and one place
            to see what was said, who owns it, and what happened next.
          </p>

          <ul className="mt-8 space-y-5">
            {CAPABILITIES.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-3.5">
                <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 flex-none
                                 rounded-lg bg-white/10 ring-1 ring-white/15">
                  <Icon className="w-4 h-4 text-white/85" />
                </span>
                <div className="min-w-0">
                  <p className="text-[14.5px] font-medium leading-snug">{title}</p>
                  <p className="text-[13px] leading-relaxed text-white/60 mt-0.5">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative hidden lg:block text-[12px] text-white/45 mt-10">
          © {BRAND_CONFIG.footer.year} {BRAND_CONFIG.footer.companyName}. All rights reserved.
        </p>
      </div>

      {/* ── Form ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-10 lg:py-14">
        <div className="w-full max-w-[380px]">
          {showForgotPassword ? (
            <>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="inline-flex items-center gap-1.5 text-[13px] text-gray-600 hover:text-gray-900 mb-6"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </button>

              <h2 className="text-[26px] font-semibold text-gray-900 tracking-[-0.02em]">Reset your password</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-gray-600">
                Enter the email on your account and we'll send a link to set a new password.
              </p>

              {forgotSuccess ? (
                <div className="mt-7 space-y-5">
                  <div className="flex gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800">
                    <CheckCircle className="w-5 h-5 flex-none mt-0.5" />
                    <div>
                      <p className="font-medium text-[14px]">Check your email</p>
                      <p className="text-[13px] mt-1 leading-relaxed">
                        If an account with that address exists, a reset link is on its way.
                      </p>
                    </div>
                  </div>
                  <button onClick={handleBackToLogin} className="tl-btn tl-btn-primary w-full py-3 text-[15px]">
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form
                  className="mt-7 space-y-5"
                  onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }}
                >
                  <div>
                    <label className={labelCls} htmlFor="forgot-email">Email address</label>
                    <input
                      id="forgot-email" type="email" autoComplete="email"
                      value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                      className={field} placeholder="you@company.com" disabled={forgotLoading}
                    />
                  </div>

                  {forgotError && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700">
                      <AlertCircle className="w-4 h-4 flex-none" />
                      <span className="text-[13px]">{forgotError}</span>
                    </div>
                  )}

                  <button
                    type="submit" disabled={forgotLoading || !forgotEmail}
                    className="tl-btn tl-btn-primary w-full py-3 text-[15px] disabled:opacity-60"
                  >
                    {forgotLoading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <h2 className="text-[26px] font-semibold text-gray-900 tracking-[-0.02em]">Sign in</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-gray-600">
                {BRAND_CONFIG.loginSubtitle}
              </p>
              {/* The only route to signup. Without a link here it is a page nobody finds. */}
              <p className="mt-2 text-[14px] text-gray-600">
                New here?{' '}
                <a href="/signup" className="text-blue-700 font-medium hover:underline">
                  Create an account
                </a>
              </p>

              <form className="mt-7 space-y-5" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                <div>
                  <label className={labelCls} htmlFor="username">Username</label>
                  <input
                    id="username" type="text" autoComplete="username"
                    value={username} onChange={(e) => setUsername(e.target.value)}
                    className={field} placeholder="Enter your username" disabled={loading}
                  />
                </div>

                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <label className={labelCls + ' mb-0'} htmlFor="password">Password</label>
                    <button
                      type="button" onClick={() => setShowForgotPassword(true)}
                      className="text-[12.5px] font-medium text-blue-700 hover:text-blue-800"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    id="password" type="password" autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className={field} placeholder="Enter your password" disabled={loading}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700">
                    <AlertCircle className="w-4 h-4 flex-none" />
                    <span className="text-[13px]">{error}</span>
                  </div>
                )}

                <button
                  type="submit" disabled={loading}
                  className="tl-btn tl-btn-primary w-full py-3 text-[15px] disabled:opacity-60"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>

              {/* Renders nothing unless providers are configured, so password
                  sign-in is never blocked by an optional feature being off. */}
              <SocialSignInButtons intent="signin" />

              {/* Disclosed, not demanded. See the note on the component. */}
              {BRAND_CONFIG.terms.enabled && (
                <p className="mt-6 text-[12px] leading-relaxed text-gray-500">
                  By signing in you agree to our{' '}
                  <a href={BRAND_CONFIG.terms.url} target="_blank" rel="noopener noreferrer"
                     className="text-gray-700 underline underline-offset-2 hover:text-gray-900">
                    {BRAND_CONFIG.terms.linkText}
                  </a>
                  {BRAND_CONFIG.terms.showPrivacy && (
                    <>
                      {' '}and{' '}
                      <a href={BRAND_CONFIG.terms.privacyUrl} target="_blank" rel="noopener noreferrer"
                         className="text-gray-700 underline underline-offset-2 hover:text-gray-900">
                        {BRAND_CONFIG.terms.privacyText}
                      </a>
                    </>
                  )}.
                </p>
              )}

              <p className="lg:hidden mt-8 text-[12px] text-gray-400">
                © {BRAND_CONFIG.footer.year} {BRAND_CONFIG.footer.companyName}. All rights reserved.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
